import { Capacitor } from "@capacitor/core";
import { runNativePrompt } from "@/lib/nativePrompts";
import { requestAppTracking, appTrackingAuthorized } from "@/lib/appTracking";
import { createNativeAdDragRelay, scrollParent } from "@/lib/nativeAdDrag";

// Google AdMob (app NATIVE uniquement) via capacitor-admob-nextgen (GMA Next-Gen SDK Android /
// Mobile Ads SDK iOS + UMP). UNIQUEMENT des annonces NATIVES (format officiel « Natif avancé », template
// Google « medium » : média, titre, texte, bouton, badge Annonce/AdChoices), rendues par le SDK :
//  - REELS : dans une SLIDE dédiée du flux — l'utilisateur glisse simplement vers le reel suivant ;
//    sans annonce disponible (no-fill, réseau…) la slide se retire, SANS aucun repli publicitaire ;
//  - FIL et PROFIL (settings.admob.feed_native, défaut actif) : carte « Annonce · Google » intégrée entre
//    les publications, jamais collée à une campagne Yalola.
// PRÉCHARGEMENT (lot 361) : l'annonce est demandée ~un écran AVANT que la slide / la carte n'arrive (posée
// invisible via showNativeAd({hidden}) + patch du plugin setNativeAdVisible / setNativeAdPosition), puis
// révélée instantanément ; elle n'est plus jetée quand la carte sort un peu de l'écran (seulement loin).
// RÉSERVOIR (lot 634) : en plus, une annonce PAR BLOC attend EN MÉMOIRE côté plugin (preloadNativeAd, sans vue) —
// remplie dès l'init du SDK (Fil & Profil) / à l'ouverture des Reels, rechargée après chaque annonce posée : le
// showNativeAd() suivant la pose instantanément (res.warm), sans attente réseau.
// Il n'existe AUCUN interstitiel plein écran dans Yalola (décision produit 2026-06 : les anciens blocs
// « Interstitial » restent seulement dans la console AdMob et ne sont jamais appelés).
// Coexistence avec les campagnes Yalola (settings.admob.reels_mode) : « complement » (défaut) = Google
// s'intercale tous les N reels MÊME quand une campagne Yalola tourne, « fallback » = Google seulement quand
// aucune campagne Yalola n'est disponible. Les Ad Unit IDs viennent de la config admin (settings.admob.android
// / .ios : reels_native_unit, feed_native_unit) — RIEN n'est codé en dur ici. Tant que admob.test_mode est
// ACTIF, ce sont les unités de TEST officielles Google qui servent : jamais de vraie annonce, rien dans la
// console AdMob. Chaque étape (plugin, SDK, consentement, chargement, affichage) est remontée au serveur
// (POST /google-ads/diagnostic).
const TEST_NATIVE = {
  android: "ca-app-pub-3940256099942544/2247696110",
  ios: "ca-app-pub-3940256099942544/3986624511",
};
// Hauteur du template natif « medium » (fixée par les templates officiels Google, en dp/pt = px CSS).
export const NATIVE_MEDIUM_HEIGHT = 350;
// Le plugin refuse une nouvelle demande native plus rapprochée que ce délai (anti trafic invalide).
const RETRY_INTERVAL_MS = 1000;
// Libellés du statut UMP (entier renvoyé par le plugin).
const CONSENT_STATUS = { 0: "inconnu", 1: "requis", 2: "non requis", 3: "obtenu" };

let mod = null;
let initialized = false;
let initFailed = false;
let canRequestAds = false;
let privacyOptionsRequired = false;
let listenersReady = false;
let nativeShowing = null;   // { onImpression, onClick, cfg, placement, el } de l'annonce native affichée
let nativeOwner = null;     // jeton de l'emplacement qui possède la vue native (UNE seule à la fois)
let nativeLoaded = false;   // une vue native est posée (visible ou cachée en préchargement)
let nativeVisible = false;  // … et visible à l'écran
let lastNativeShowAt = 0;   // horodatage de la dernière demande native (respect de RETRY_INTERVAL_MS)
let nativePending = false;  // une demande native est EN VOL : personne ne peut prendre la vue tant qu'elle n'est pas réglée (lot 575)
let lastTestMode = true;
const SESSION = Math.random().toString(36).slice(2, 10);
const reported = new Map();  // étape → nombre d'envois (plafond par session)
let lastFailure = null;      // dernière étape en échec (« load_failed · No fill ») — pour l'admin

// Motif du dernier échec (étape · détail), null si tout s'est bien passé jusqu'ici.
export const lastAdmobFailure = () => lastFailure;

// QA préversion (web, dev uniquement) : localStorage yalola.qa_admob=1 → les emplacements natifs se mettent
// en page comme sur Android avec une fausse annonce (le plugin web ne dessine rien). Jamais en production.
export const admobQaMode = () => {
  try {
    return process.env.NODE_ENV !== "production" && !Capacitor.isNativePlatform() && localStorage.getItem("yalola.qa_admob") === "1";
  } catch { return false; }
};

export function admobConfig(settings) {
  const qa = admobQaMode();
  if (!Capacitor.isNativePlatform() && !qa) return null;
  const platform = qa ? "android" : Capacitor.getPlatform(); // 'android' | 'ios'
  const admob = settings?.admob || {};
  const cfg = admob[platform] || {};
  if (cfg.enabled !== true) return null;
  lastTestMode = admob.test_mode !== false;
  return { platform, test: lastTestMode, qa, ...cfg };
}

// « complement » (défaut) | « fallback » — voir l'en-tête du fichier.
export function reelsAdMode(settings) {
  return settings?.admob?.reels_mode === "fallback" ? "fallback" : "complement";
}

// placement ∈ { "reels", "feed", "profile" } — Fil et Profil partagent le bloc « Fil & Profil natif ».
const nativeUnitFor = (cfg, placement) => {
  if (cfg.test) return TEST_NATIVE[cfg.platform];
  return ((placement === "reels" ? cfg.reels_native_unit : cfg.feed_native_unit) || "").trim();
};

// Fil / Profil : annonces natives Google actives sur cet appareil (plateforme activée, interrupteur admin
// « Fil & Profil » non coupé, bloc renseigné ou mode test).
export function feedGoogleEnabled(settings) {
  const cfg = admobConfig(settings);
  if (!cfg || settings?.admob?.feed_native === false) return false;
  return !!nativeUnitFor(cfg, "feed");
}

// Fil : une annonce Google tous les N posts (réglage admin par plateforme) ; 0 si inactif.
export function feedGoogleEvery(settings) {
  return feedGoogleEnabled(settings) ? Math.max(3, Number(admobConfig(settings).feed_frequency) || 6) : 0;
}

// Remontée d'une étape au serveur (meilleur effort, max 5 fois par étape et par session).
async function report(step, ok = true, extra = {}) {
  if (!ok) lastFailure = extra.detail ? `${step} · ${extra.detail}` : step;
  if (admobQaMode()) return;
  const n = reported.get(step) || 0;
  if (n >= 5) return;
  reported.set(step, n + 1);
  try {
    const { api } = await import("@/lib/api");
    const { getAppVersion } = await import("@/lib/appVersion");
    const v = await getAppVersion();
    api.post("/google-ads/diagnostic", {
      platform: Capacitor.getPlatform(), step, ok, test_mode: lastTestMode, session: SESSION,
      app_version: `${v.version} (${v.build})${v.web ? ` · ${v.web.split(" · ")[0]}` : ""}`, ...extra,
    }).catch(() => {});
  } catch { /* meilleur effort */ }
}

const errText = (e) => (e && (e.message || e.error || e.code !== undefined)
  ? `${e.message || e.error || ""}${e.code !== undefined ? ` (code ${e.code})` : ""}`.trim()
  : String(e || ""));

async function plugin() {
  if (!mod) {
    try { mod = await import("capacitor-admob-nextgen"); }
    catch (e) {
      // Build natif antérieur au plugin : rien ne pourra s'afficher tant que l'app n'est pas rebuildée.
      report("plugin_missing", false, { detail: errText(e) });
      throw e;
    }
  }
  return mod;
}

async function countImpression(cfg, placement) {
  if (cfg.qa) return;
  try {
    const { api } = await import("@/lib/api");
    api.post("/google-ads/impression", { platform: cfg.platform, placement }).catch(() => {});
  } catch { /* meilleur effort */ }
}

// Écouteurs du plugin (posés une fois) : impression native → comptage pour le tableau de bord admin,
// clic → rappel de l'emplacement, glissement commencé sur l'annonce (iOS, patch Yalola) → défilement du
// conteneur de l'emplacement (page pour le Fil / Profil, flux plein écran pour les Reels).
const dragTarget = () => {
  const n = nativeShowing;
  if (!n?.el) return null;
  return { el: scrollParent(n.el), snap: n.placement === "reels" };
};

async function ensureListeners() {
  if (listenersReady) return;
  const { AdMobNextGen } = await plugin();
  if (typeof AdMobNextGen.addListener !== "function") return;
  listenersReady = true;
  AdMobNextGen.addListener("onNativeAdImpression", () => { const n = nativeShowing; if (n) { countImpression(n.cfg, `${n.placement}_native`); n.onImpression?.(); } });
  AdMobNextGen.addListener("onNativeAdClicked", () => { nativeShowing?.onClick?.(); });
  AdMobNextGen.addListener("onNativeAdDrag", createNativeAdDragRelay(dragTarget));
}

// Google UMP (le message de consentement publié dans la console AdMob — jamais un popup RGPD maison).
// Appelé à chaque lancement : met à jour l'état du consentement et affiche le formulaire Google quand
// la réglementation (EEE/UK/CH) l'exige. Les annonces ne sont autorisées QUE si UMP dit canRequestAds.
// Lot 714 (Apple 5.1.1(iv)/5.1.2) — `attRefused` (iOS, « Demander à l'app de ne pas me suivre ») : le formulaire
// Google (« cookies… 210 partenaires », bouton Autoriser = consentement TCF au suivi) n'est PAS présenté — il
// proposerait de contourner le refus Apple. L'état UMP est seulement LU : hors EEE il vaut « non requis » (annonces
// limitées, sans IDFA ni personnalisation) ; en EEE il reste « requis » → canRequestAds=false → aucune annonce Google
// tant que le membre n'ouvre pas lui-même « Options de confidentialité des annonces » (Réglages).
async function gatherUmpConsent(cfg, attRefused = false) {
  const { AdMobNextGen } = await plugin();
  // En mode test, on force la géographie EEE sur les APPAREILS DE TEST Google pour pouvoir
  // vérifier le formulaire depuis n'importe où. Aucun effet sur les appareils des vrais membres.
  const showFormIfRequired = !attRefused;
  const opts = cfg.test ? { debug: true, showFormIfRequired } : { showFormIfRequired };
  let info = null;
  try {
    info = await AdMobNextGen.requestConsentInfo(opts);
  } catch (e) {
    console.warn("UMP consent flow failed:", e?.message || e);
  }
  privacyOptionsRequired = info?.isPrivacyOptionsRequired === true || info?.privacyOptionsRequirementStatus === "REQUIRED";
  canRequestAds = info ? info.canRequestAds === true : false;
  const att = attRefused ? " · ATT refusé → formulaire non affiché, personnalisation coupée" : "";
  report("consent", canRequestAds, { detail: info ? `statut ${CONSENT_STATUS[info.consentStatus] ?? info.consentStatus ?? "?"}${att}` : `flux UMP en échec${att}` });
  return canRequestAds;
}

export async function initAdMob(settings) {
  const cfg = admobConfig(settings);
  if (!cfg || initFailed) return false;
  if (initialized && canRequestAds) return true;
  try {
    const { AdMobNextGen } = await plugin();
    // iOS : ATT (Apple, obligation distincte du RGPD/UMP) EN PREMIER, avant toute mesure du SDK — sans condition
    // sur UMP ni sur l'admin. Idempotent : déjà répondu à l'écran Bienvenue → statut renvoyé sans dialogue.
    // Lot 714 : la RÉPONSE compte — tout statut autre que « authorized » (refusé, restreint, non déterminé, erreur)
    // est traité comme un refus de suivi (Apple 5.1.2 : « aucun autre moyen »).
    const attRefused = cfg.platform === "ios" && !appTrackingAuthorized(await requestAppTracking());
    // Ordre exigé par le SDK Next-Gen : 1) consentement UMP, 2) initialize() (toujours, même sans consentement :
    // le SDK démarre avec le statut recueilli), 3) demandes d'annonces UNIQUEMENT si canRequestAds.
    // File des dialogues natifs : ATT → consentement → permission push. Déjà initialisé + consentement encore
    // manquant → on rejoue seulement le flux UMP.
    await runNativePrompt("consent", () => gatherUmpConsent(cfg, attRefused));
    if (!initialized) {
      // disablePersonalization (patch Yalola du plugin iOS) → MobileAds.requestConfiguration
      // .publisherPrivacyPersonalizationState = .disabled : AUCUNE annonce personnalisée pour toute la session, quel que
      // soit le consentement UMP (même rouvert plus tard depuis les Réglages) — le refus ATT ne peut pas être contourné.
      await AdMobNextGen.initialize({ isTesting: cfg.test, disablePersonalization: attRefused });
      initialized = true;
      report("init_ok", true, { detail: `${cfg.test ? "mode TEST (unités Google de test)" : "mode RÉEL"}${attRefused ? " · personnalisation désactivée (ATT refusé)" : ""}` });
    }
    await ensureListeners();
    // Lot 634 : RÉSERVOIR — dès que le SDK est prêt (consentement obtenu), une annonce du bloc « Fil & Profil » est
    // chargée en mémoire : la première carte du Fil s'affiche sans attente réseau.
    if (canRequestAds && feedGoogleEnabled(settings)) preloadNativeAd(settings, "feed");
  } catch (e) {
    initFailed = true;
    console.warn("AdMob init failed:", e?.message || e);
    report("init_failed", false, { detail: errText(e) });
  }
  return initialized && canRequestAds;
}

// Point d'entrée « options de confidentialité » UMP : Google EXIGE que l'app permette de rouvrir
// ses choix de consentement quand privacyOptionsRequirementStatus vaut REQUIRED.
export async function adsPrivacyOptionsRequired(settings) {
  if (!admobConfig(settings)) return false;
  if (!initialized && !initFailed) await initAdMob(settings);
  return privacyOptionsRequired;
}

export async function showAdsPrivacyOptions() {
  try {
    const { AdMobNextGen } = await plugin();
    await AdMobNextGen.showPrivacyOptionsForm();
    return true;
  } catch (e) {
    console.warn("UMP privacy options form:", e?.message || e);
    return false;
  }
}

export function reelsAdFrequency(settings) {
  const cfg = admobConfig(settings);
  return cfg ? Math.max(3, Number(cfg.reels_frequency) || 8) : 0;
}

// Plan des SLIDES natives Google dans la liste des Reels — fonction pure.
// Parcourt la liste déjà construite (reels + pubs Yalola `_isAd`) et insère une slide Google après
// chaque série de `freq` reels organiques, jamais collée à une pub Yalola (ni juste avant, ni juste
// après : on décale d'un reel en gardant le compteur). Mode « fallback » + campagne Yalola → aucune
// slide. Les pubs Yalola ne remettent pas le compteur à zéro. Retourne une nouvelle liste.
export function insertGoogleReelSlides(list, { settings, yalolaAds = 0 } = {}) {
  const freq = reelsAdFrequency(settings);
  if (!freq) return list;
  if (yalolaAds > 0 && reelsAdMode(settings) === "fallback") return list;
  const out = [];
  let count = 0;
  for (let i = 0; i < list.length; i++) {
    const it = list[i];
    out.push(it);
    if (it._isAd) continue;
    count += 1;
    const next = list[i + 1];
    if (count >= freq && !(next && next._isAd)) {
      out.push({ _key: `gad-${it._key || i}`, _isAd: true, _isGoogle: true, id: `gad-${it._key || i}` });
      count = 0;
    }
  }
  return out;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// RÉSERVOIR (lot 634) : une annonce native PAR BLOC chargée EN MÉMOIRE par le plugin (patch preloadNativeAd, sans vue),
// que le prochain showNativeAd() du même bloc pose instantanément (res.warm = true, aucune attente réseau). Rempli dès
// l'initialisation du SDK (Fil & Profil), à l'ouverture des Reels, puis RECHARGÉ après chaque annonce posée. Le plugin
// jette une annonce de plus de ~55 min (expiration AdMob). Résout true si une annonce attend (ou se charge) dans le
// réservoir ; false sinon (plugin ancien, no-fill, hors app native…). Une seule demande en vol par bloc.
const warmInflight = new Map();
export async function preloadNativeAd(settings, placement) {
  const cfg = admobConfig(settings);
  if (!cfg || cfg.qa) return false;
  const adUnitId = nativeUnitFor(cfg, placement);
  if (!adUnitId) return false;
  if (warmInflight.has(adUnitId)) return warmInflight.get(adUnitId);
  const run = (async () => {
    try {
      if (!(await initAdMob(settings))) return false;
      const { AdMobNextGen } = await plugin();
      if (typeof AdMobNextGen.preloadNativeAd !== "function") return false; // build natif antérieur au réservoir
      // Le plugin refuse deux demandes réseau à moins de RETRY_INTERVAL_MS : on patiente.
      const wait = lastNativeShowAt + RETRY_INTERVAL_MS + 50 - Date.now();
      if (wait > 0) await sleep(wait);
      lastNativeShowAt = Date.now();
      const res = await AdMobNextGen.preloadNativeAd({ adUnitId, retryInterval: RETRY_INTERVAL_MS });
      if (res?.ready && !res.cached) report("preload_ok", true, { detail: `réservoir ${placement} rempli en ${Math.round(res.ms || 0)} ms (${cfg.test ? "unité de test Google" : "unité réelle"})`, format: "native", placement });
      return !!(res?.ready || res?.loading);
    } catch (e) {
      report("preload_failed", false, { detail: errText(e), code: e?.code, format: "native", placement });
      return false;
    } finally {
      warmInflight.delete(adUnitId);
    }
  })();
  warmInflight.set(adUnitId, run);
  return run;
}
// Une demande de réservoir est-elle en vol pour ce bloc ? (tests / diagnostic)
export const nativeAdWarming = (settings, placement) => { const cfg = admobConfig(settings); return !!cfg && warmInflight.has(nativeUnitFor(cfg, placement)); };

// Charge une annonce NATIVE (template « medium ») dans le cadre `rect` de l'emplacement `placement`
// (« reels » | « feed » | « profile »). rect = { x, y, width } en px CSS, y ABSOLU dans le document
// (top + scrollY) : le plugin suit lui-même le défilement de la page. `owner` = jeton de l'emplacement
// (une seule vue native à la fois). `hidden` = PRÉCHARGEMENT : l'annonce est chargée et posée invisible,
// prête avant que le membre n'arrive dessus ; setNativeAdVisible(owner, true) la révèle sans nouvelle
// demande réseau (l'impression n'est comptée qu'à ce moment-là). Résout { width, height } quand l'annonce
// est rendue ; { busy: true } si un AUTRE emplacement tient déjà une vue visible (ou une vue chargée,
// pour un préchargement) — on réessaiera ; null sinon (pas d'annonce, plugin absent…) → l'emplacement
// se retire de l'écran. `el` = élément du cadre (pour faire défiler son conteneur quand un glissement commence sur
// l'annonce — iOS).
export async function showNativeAd(settings, placement, rect, { owner, hidden = false, clip, el, onImpression, onClick } = {}) {
  const cfg = admobConfig(settings);
  if (!cfg) return null;
  const adUnitId = nativeUnitFor(cfg, placement);
  if (!adUnitId) { report("unit_missing", false, { detail: `aucun Ad Unit ID natif (${placement === "reels" ? "Reels" : "Fil & Profil"})` }); return null; }
  if (!(await initAdMob(settings))) return null;
  const token = owner ?? Symbol("native-ad");
  // Une seule vue native (SDK) : on ne vole jamais la vue d'un emplacement VISIBLE, et un préchargement ne
  // remplace pas une annonce déjà chargée (même cachée) par un autre emplacement.
  // Lot 575 : ni la vue d'une demande EN VOL — sinon la demande doublée aboutissait quand même côté SDK et posait une
  // vue que plus personne ne pilotait (annonce « collée » sur une publication, puis au-dessus des Reels).
  const taken = () => nativeOwner !== null && nativeOwner !== token && (nativePending || nativeVisible || (hidden && nativeLoaded));
  if (taken()) return { busy: true };
  // Le plugin refuse deux demandes à moins de RETRY_INTERVAL_MS : on patiente au lieu d'échouer.
  const wait = lastNativeShowAt + RETRY_INTERVAL_MS + 50 - Date.now();
  if (wait > 0) await sleep(wait);
  if (taken()) return { busy: true };
  lastNativeShowAt = Date.now();
  nativeOwner = token;
  nativeLoaded = false;
  nativeVisible = false;
  nativePending = true;
  nativeShowing = { onImpression, onClick, cfg, placement, el: el || null };
  let AdMobNextGen = null;
  try {
    ({ AdMobNextGen } = await plugin());
    const res = await AdMobNextGen.showNativeAd({
      adUnitId, template: "medium", retryInterval: RETRY_INTERVAL_MS,
      x: Math.round(rect.x), y: Math.round(rect.y), width: Math.round(rect.width), hidden: !!hidden, ...clipOptions(clip),
    });
    nativePending = false;
    if (nativeOwner !== token) {
      // La vue a été rendue pendant la demande (emplacement parti) : le SDK vient pourtant de la poser → on la retire,
      // sinon elle resterait à l'écran sans pilote. (Personne d'autre ne peut la posséder : demande en vol = exclusive.)
      if (nativeOwner === null) { try { await AdMobNextGen.hideNativeAd(); } catch { /* rien à retirer */ } }
      return { busy: true };
    }
    nativeLoaded = true;
    nativeVisible = !hidden;
    if (cfg.qa) return { width: Math.round(rect.width), height: NATIVE_MEDIUM_HEIGHT, qa: true };
    if (!res || !(res.height > 0)) throw new Error("annonce native non rendue");
    const unit = cfg.test ? "unité de test Google" : "unité réelle";
    // Annonce avec vidéo ? (plugin patché : hasVideo ; absent sur les anciens builds → non remonté)
    const hasVideo = typeof res.hasVideo === "boolean" ? res.hasVideo : undefined;
    if (nativeShowing) nativeShowing.hasVideo = hasVideo;
    const media = hasVideo === undefined ? "" : hasVideo ? ", vidéo" : ", image";
    // Lot 634 : sortie du réservoir (aucune attente réseau) → remonté à l'admin ; puis le réservoir est RECHARGÉ pour
    // l'annonce suivante de ce bloc (dans tous les cas : il vient d'être vidé ou n'avait rien pour ce bloc).
    const warm = res.warm === true;
    report("load_ok", true, { detail: `${unit} (native ${placement}${hidden ? ", préchargée" : ""}${warm ? ", réservoir" : ""}${media})`, format: "native", placement, has_video: hasVideo, warm });
    if (!hidden) report("shown", true, { detail: `${cfg.test ? "annonce native de TEST" : "annonce native réelle"}${media}`, format: "native", placement, has_video: hasVideo });
    preloadNativeAd(settings, placement);
    return { width: res.width || Math.round(rect.width), height: res.height, hasVideo, warm: warm || undefined };
  } catch (e) {
    nativePending = false;
    if (nativeOwner === token) { nativeOwner = null; nativeShowing = null; nativeLoaded = false; nativeVisible = false; }
    // Balayage : plus aucun emplacement ne possède de vue → toute vue encore attachée côté SDK est orpheline → retirée.
    if (nativeOwner === null && AdMobNextGen) { try { await AdMobNextGen.hideNativeAd(); } catch { /* rien à retirer */ } }
    report("load_failed", false, { detail: errText(e), code: e?.code, format: "native", placement });
    console.warn("AdMob native ad:", e?.message || e);
    return null;
  }
}

// Emplacement visible depuis plusieurs secondes SANS annonce (vue gardée ailleurs, plugin muet…) → diagnostic admin.
export function reportNativeSlotStuck(placement, detail) {
  report("slot_stuck", false, { detail, format: "native", placement });
}

// Jeton de l'emplacement qui possède actuellement la vue native (null si aucune).
export const nativeAdOwner = () => nativeOwner;
// Une vue native est-elle visible à l'écran en ce moment ?
export const nativeAdVisible = () => nativeVisible;

// Lot 540 — défilement que le plugin SOUSTRAIT pour poser la vue (Android WebView.getScrollY / iOS contentOffset).
// Sur Android, Chromium promeut le conteneur plein écran des Reels en « root scroller » : la WebView rapporte alors le
// scrollTop de CE conteneur alors que window.scrollY reste 0 → une vue ancrée sur window.scrollY partait à
// -(scrollTop) px (sonde 2.1 : « visible à y=-13963 (attendu 223), scrollY natif 14186 »). Le cadre est donc ancré sur
// la valeur NATIVE ; sans sonde (ancien build, web) on retombe sur window.scrollY (identique partout ailleurs).
export async function nativeScrollY() {
  try {
    const { AdMobNextGen } = await plugin();
    if (typeof AdMobNextGen?.getNativeAdState !== "function") return window.scrollY;
    const st = await AdMobNextGen.getNativeAdState();
    return st && !st.web && Number.isFinite(st.scrollY) ? st.scrollY : window.scrollY;
  } catch { return window.scrollY; }
}

// Révèle (true) ou cache (false) la vue native déjà chargée de `owner` — aucune nouvelle demande réseau.
// Sans effet si un autre emplacement possède la vue. Résout true si l'ordre a été transmis au plugin.
export async function setNativeAdVisible(owner, visible) {
  if (nativeOwner === null || nativeOwner !== owner || !mod) return false;
  nativeVisible = !!visible;
  if (visible && nativeShowing) {
    const n = nativeShowing;
    const media = n.hasVideo === undefined ? "" : n.hasVideo ? ", vidéo" : ", image";
    report("shown", true, { detail: `${n.cfg.test ? "annonce native de TEST (préchargée)" : "annonce native réelle (préchargée)"}${media}`, format: "native", placement: n.placement, has_video: n.hasVideo });
  }
  try { await mod.AdMobNextGen.setNativeAdVisible({ visible: !!visible }); return true; } catch { return false; }
}

// Bande visible { top, bottom } (px CSS, repère de la fenêtre) → options du plugin ; -1 = pas de découpe.
// La vue native est dessinée PAR-DESSUS la WebView : sans découpe elle passerait devant l'en-tête collant, la
// barre du bas ou la barre d'état quand la carte glisse dessous. Le plugin réapplique la découpe à chaque
// défilement natif (Fil) — aucune latence JS.
const clipOptions = (clip) => ({
  clipTop: clip && Number.isFinite(clip.top) ? Math.round(clip.top) : -1,
  clipBottom: clip && Number.isFinite(clip.bottom) ? Math.round(clip.bottom) : -1,
});

// Déplace la vue native de `owner` (même repère que showNativeAd) sans la recharger : la page a bougé
// (image chargée au-dessus, slide de Reels arrivée à l'écran…) ; `clip` = nouvelle bande visible.
// Sans effet pour un autre emplacement.
export async function moveNativeAd(owner, rect, clip) {
  if (nativeOwner === null || nativeOwner !== owner || !mod) return false;
  try { await mod.AdMobNextGen.setNativeAdPosition({ x: Math.round(rect.x), y: Math.round(rect.y), ...clipOptions(clip) }); return true; } catch { return false; }
}

// SONDE (2.0.1) : état RÉEL de la vue native après une révélation, comparé au cadre attendu `rect` (px CSS, repère
// de la fenêtre). Résout { ok, detail, state } — ok=false quand la vue n'est pas attachée / pas visible / découpée à
// zéro / posée à plus de PROBE_TOLERANCE_PX du cadre ; null si la sonde n'existe pas (ancien build) ou en QA web.
// Le détail est remonté dans le diagnostic admin (« probe_ok » / « probe_fail »).
const PROBE_TOLERANCE_PX = 6;
export async function probeNativeAd(owner, rect, placement) {
  if (nativeOwner === null || nativeOwner !== owner || !mod || typeof mod.AdMobNextGen?.getNativeAdState !== "function") return null;
  let st;
  try { st = await mod.AdMobNextGen.getNativeAdState(); } catch { return null; }
  if (!st || st.web || nativeOwner !== owner) return null;
  const dy = Number.isFinite(st.y) ? Math.round(st.y - rect.y) : null;
  const dx = Number.isFinite(st.x) ? Math.round(st.x - rect.x) : null;
  const problems = [];
  if (!st.attached) problems.push("vue non attachée");
  if (st.attached && st.visible === false) problems.push("vue INVISIBLE");
  if (st.clipEmpty) problems.push("découpe vide");
  if (dy !== null && Math.abs(dy) > PROBE_TOLERANCE_PX) problems.push(`écart vertical ${dy} px`);
  if (dx !== null && Math.abs(dx) > PROBE_TOLERANCE_PX) problems.push(`écart horizontal ${dx} px`);
  if (st.attached && Number.isFinite(st.height) && st.height <= 0) problems.push("hauteur 0");
  const ok = problems.length === 0;
  const pos = `vue ${st.attached ? "attachée" : "absente"}${st.visible === undefined ? "" : st.visible ? ", visible" : ", invisible"} à y=${Math.round(st.y ?? -1)} (attendu ${Math.round(rect.y)})`;
  const clipTxt = st.clipTop === undefined ? "sans découpe" : `découpe ${Math.round(st.clipTop)}→${Math.round(st.clipBottom)}${st.clipEmpty ? " (VIDE)" : ""}`;
  const detail = `${pos}, ${clipTxt}, ${Math.round(st.width ?? 0)}×${Math.round(st.height ?? 0)}, scrollY natif ${Math.round(st.scrollY ?? 0)}${ok ? "" : " — " + problems.join(", ")}`;
  report(ok ? "probe_ok" : "probe_fail", ok, { detail, format: "native", placement });
  return { ok, detail, state: st };
}

// Retire la vue native. Avec `owner`, ne fait rien si un AUTRE emplacement possède désormais la vue
// (sinon un emplacement qui quitte l'écran effacerait l'annonce du suivant). Toujours sans erreur.
export async function hideNativeAd(owner) {
  if (owner !== undefined && nativeOwner !== null && nativeOwner !== owner) return;
  // Demande en vol pour cet emplacement : on marque la vue « à personne » ; showNativeAd la retirera dès que le SDK l'aura posée.
  nativeOwner = null;
  nativeShowing = null;
  nativeLoaded = false;
  nativeVisible = false;
  if (!mod) return;
  try { await mod.AdMobNextGen.hideNativeAd(); } catch { /* rien à retirer */ }
}

// Mode « repli seul » + campagne Yalola en diffusion → Google volontairement non appelé (remonté
// une fois par session pour que l'admin comprenne pourquoi rien ne s'affiche).
export function reportAdmobSkippedByYalola(settings) {
  if (!admobConfig(settings)) return;
  report("skipped_yalola", true, { detail: "campagne Yalola en diffusion — mode repli seul" });
}
